import { useMemo, ReactNode } from 'react'
import Link from 'next/link'

import { authors, Author as AuthorsModuleAuthor } from '@/authors'
import { ProductBanner } from '@/components/ProductBanner'

import { TableOfContents } from '@/components/TableOfContents'
import {
  ArticleContainer,
  PostContainer,
} from '@/components/marketing/Containers'
import { AuthorsFull } from '@/components/marketing/Authors'
import { SubscribeFooter } from './SubscribeFooter'
import { blogConvertKitFormId, changelogConvertKitFormId } from '@/config'

interface GoBackProps {
  href: string
  text: string
}

export function GoBack({ href, text }: GoBackProps) {
  return (
    <div className="mt-[-62px] flex px-4 pb-10 pt-8 lg:px-8">
      <Link href={href}>
        <span className="group flex text-sm font-semibold leading-6 text-slate-200 hover:text-white">
          <svg
            viewBox="0 -9 3 24"
            className="mr-3 h-6 w-auto overflow-visible text-slate-400 group-hover:text-slate-300"
          >
            <path
              d="M3 0L0 3L3 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {text}
        </span>
      </Link>
    </div>
  )
}

interface Author extends AuthorsModuleAuthor {}

interface TableOfContentsItem {
  id: string
  title: string
  children: TableOfContentsItem[]
}

interface BlogMeta {
  title: string
  hideProductBanner?: boolean
  hideAuthorCard?: boolean
  hideCTA?: boolean
  hideTableOfContents?: boolean
  authorNames?: string[]
  authors: Author[]
  // Add other blog metadata as needed
  [key: string]: unknown
}

interface BlogLayoutProps {
  children: ReactNode
  tableOfContents: TableOfContentsItem[]
  blogMeta: BlogMeta
  isChangelog?: boolean
}

export function BlogLayout({
  children,
  tableOfContents,
  blogMeta,
  isChangelog = false,
}: BlogLayoutProps) {
  const hideProductBanner = blogMeta.hideProductBanner ?? false
  const hideAuthorCard = (blogMeta.hideAuthorCard || isChangelog) ?? false
  const hideCTA = blogMeta.hideCTA ?? false
  const _hideTableOfContents = blogMeta.hideTableOfContents ?? false
  const hideTableOfContents =
    tableOfContents.length == 0 || _hideTableOfContents
  const containerWidth = hideTableOfContents ? 'max-w-6xl' : 'max-w-8xl'

  const authorsData = useMemo<Author[]>(() => {
    const authorNames = blogMeta.authorNames || []
    return authorNames.map(
      (author_name) => authors[author_name as keyof typeof authors]
    )
  }, [blogMeta.authorNames])

  blogMeta.authors = authorsData || []

  return (
    <>
      {isChangelog ? (
        <GoBack href="/changelog" text="View full changelog" />
      ) : (
        <GoBack href="/posts" text="All posts" />
      )}

      <PostContainer className={containerWidth}>
        <div className="min-w-0 max-w-5xl flex-auto px-4 py-0 lg:pl-8 lg:pr-0 xl:px-14">
          <ArticleContainer blogMeta={blogMeta} proseChildren={children} />
          {blogMeta.authors.length > 0 && !hideAuthorCard && (
            <AuthorsFull authors={blogMeta.authors} />
          )}
        </div>
        {!hideTableOfContents && (
          <TableOfContents
            tableOfContents={tableOfContents}
            showCTA={!hideCTA}
          ></TableOfContents>
        )}
      </PostContainer>

      <PostContainer className="max-w-8xl overflow-hidden">
        <div className="min-w-0 flex-auto px-4 py-0 lg:pl-8 lg:pr-0 xl:px-14">
          {!hideProductBanner && <ProductBanner />}
          {isChangelog ? (
            <SubscribeFooter
              title="Want to get notified when we release new features?"
              convertKitFormID={changelogConvertKitFormId}
            />
          ) : (
            <SubscribeFooter
              title="Want to hear about our latest Snowflake learnings?"
              convertKitFormID={blogConvertKitFormId}
            />
          )}
        </div>
      </PostContainer>
    </>
  )
}
